import classNames from "classnames"
import { Fragment, HTMLAttributes, MouseEvent, useState } from "react"
import * as Icons from "@nova/component-icons"

type ControlsProps = HTMLAttributes<HTMLElement> & {
    backgroundColor?: string
    borderColor?: string
    showSeparator?: boolean
    items: ControlItemProps[]
}

export const Controls = ({
    backgroundColor = "#333333E5",
    borderColor = "#5D5D5D",
    showSeparator = false,
    items,
    ...rest
}: ControlsProps) => {
    return (
        <div
            {...rest}
            className={classNames(
                `h-6 flex flex-row border border-solid rounded-md overflow-hidden cursor-pointer`,
                rest.className
            )}
            style={{
                borderColor,
                backgroundColor,
                ...rest.style,
            }}
        >
            {items.map((item, i) => (
                <Fragment key={i}>
                    <ControlItem {...item} key={i} />
                    {showSeparator && i < items.length - 1 && (
                        <div
                            className="flex w-px h-6"
                            style={{ backgroundColor: borderColor }}
                            key={`${i}|`}
                        />
                    )}
                </Fragment>
            ))}
        </div>
    )
}

type ControlItemProps = HTMLAttributes<HTMLElement> & {
    Icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>
    className?: string

    /**
     * If undefined, item will not be selectable and will not dim the icon
     */
    isSelected?: boolean

    backgroundColorSelected?: string
    backgroundColorHover?: string
    allowClickAndHold?: boolean
    onClick: (e: MouseEvent) => void

    isLargeIcon?: boolean
}

export const ControlItem = ({
    Icon,
    className,
    isSelected,
    backgroundColorSelected = "#5D5D5D",
    backgroundColorHover = "#5D5D5DBF",
    allowClickAndHold = false,
    onClick,
    isLargeIcon = false,
    ...rest
}: ControlItemProps) => {
    const [isHovering, setIsHovering] = useState(false)
    const [intervalId, setIntervalId] = useState<NodeJS.Timeout>()
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>()

    const handleMouseDown = (e: MouseEvent) => {
        if (!allowClickAndHold) return

        onClick(e)

        const delayId = setTimeout(() => {
            const timeoutId = setInterval(() => {
                onClick(e)
            }, 75)
            setIntervalId(timeoutId)
        }, 250)

        setTimeoutId(delayId)
    }

    const handleMouseUp = (e: MouseEvent) => {
        if (!allowClickAndHold) {
            onClick(e)
            return
        }

        // Clear the interval when mouse is released
        if (intervalId) clearInterval(intervalId)
        if (timeoutId) clearTimeout(timeoutId)
    }

    return (
        <div
            {...rest}
            className={classNames("flex justify-center items-center h-full w-6", {
                [`filter grayscale opacity-50`]: isSelected === false,
            })}
            style={{
                backgroundColor: isSelected
                    ? backgroundColorSelected
                    : isHovering
                      ? backgroundColorHover
                      : undefined,
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            {isLargeIcon ? (
                <Icons.Icon Icon={Icon} className={className} />
            ) : (
                <Icons.ExtraSmallIcon Icon={Icon} className={className} />
            )}
        </div>
    )
}
